import React from 'react';
import Icon from '../../../components/AppIcon';

const ComplianceReport = ({ data }) => {
  const getComplianceStatus = (percentage) => {
    if (percentage >= 95) return { status: 'excellent', color: 'text-success', bg: 'bg-success/10' };
    if (percentage >= 85) return { status: 'good', color: 'text-warning', bg: 'bg-warning/10' };
    if (percentage >= 70) return { status: 'needs-attention', color: 'text-accent', bg: 'bg-accent/10' };
    return { status: 'critical', color: 'text-error', bg: 'bg-error/10' };
  };

  const getStatusIcon = (status) => {
    switch (status) {
      case 'excellent': return 'CheckCircle';
      case 'good': return 'AlertCircle';
      case 'needs-attention': return 'AlertTriangle';
      case 'critical': return 'XCircle';
      default: return 'Circle';
    }
  };

  return (
    <div className="bg-card border border-border rounded-lg p-6 shadow-light">
      <div className="flex items-center justify-between mb-6">
        <div>
          <h3 className="text-lg font-semibold text-foreground">Compliance Dashboard</h3>
          <p className="text-sm text-muted-foreground">SLA compliance and audit-ready metrics</p>
        </div>
        <div className="flex items-center space-x-2 text-sm text-muted-foreground">
          <Icon name="Shield" size={16} />
          <span>Last updated: {new Date()?.toLocaleDateString()}</span>
        </div>
      </div>
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
        {data?.map((item, index) => {
          const compliance = getComplianceStatus(item?.percentage);
          
          return (
            <div key={index} className={`p-4 rounded-lg border ${compliance?.bg} border-border`}>
              <div className="flex items-center justify-between mb-3">
                <div className="flex items-center space-x-2">
                  <Icon 
                    name={getStatusIcon(compliance?.status)} 
                    size={20} 
                    className={compliance?.color} 
                  />
                  <h4 className="font-medium text-foreground">{item?.category}</h4>
                </div>
                <span className={`text-lg font-semibold ${compliance?.color}`}>
                  {item?.percentage}%
                </span>
              </div>
              <div className="space-y-2">
                <div className="flex justify-between text-sm">
                  <span className="text-muted-foreground">Target:</span>
                  <span className="text-foreground">{item?.target}%</span>
                </div>
                <div className="flex justify-between text-sm">
                  <span className="text-muted-foreground">Incidents:</span>
                  <span className="text-foreground">{item?.total}</span>
                </div>
                <div className="flex justify-between text-sm">
                  <span className="text-muted-foreground">Compliant:</span>
                  <span className="text-foreground">{item?.compliant}</span>
                </div>
              </div>
              <div className="mt-3 pt-3 border-t border-border">
                <div className="w-full bg-muted rounded-full h-2">
                  <div 
                    className={`h-2 rounded-full transition-all duration-300 ${
                      compliance?.status === 'excellent' ? 'bg-success' :
                      compliance?.status === 'good' ? 'bg-warning' :
                      compliance?.status === 'needs-attention' ? 'bg-accent' : 'bg-error'
                    }`}
                    style={{ width: `${item?.percentage}%` }}
                  ></div>
                </div>
                <p className="text-xs text-muted-foreground mt-1">{item?.description}</p>
              </div>
            </div>
          );
        })}
      </div>
      <div className="mt-6 p-4 bg-muted rounded-lg">
        <div className="flex items-start space-x-3">
          <Icon name="Info" size={16} className="text-primary mt-0.5" />
          <div className="flex-1">
            <h4 className="text-sm font-medium text-foreground mb-1">Compliance Notes</h4>
            <ul className="text-xs text-muted-foreground space-y-1">
              <li>• All metrics are calculated based on the selected time range and filters</li>
              <li>• SLA compliance is measured against predefined response and resolution times</li>
              <li>• Reports include timestamp verification for audit trail integrity</li>
              <li>• Data integrity validation ensures compliance with regulatory requirements</li>
            </ul>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ComplianceReport;